import React, { useEffect, useRef } from 'react';
import { formatDraftTime } from '../hooks/useOwnerDraft';

/**
 * Step header for the owner (partner) forms: "Step 2 of 9 · Location", a
 * progress bar and a scrollable row of step pills that keeps the current one
 * in view on a phone. Admin forms keep their own tab strip.
 */
export const OwnerFormSteps = ({ tabs, activeTab, onSelect }) => {
  const rowRef = useRef(null);
  const index = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));
  const current = tabs[index] || tabs[0];

  useEffect(() => {
    const row = rowRef.current;
    const pill = row?.querySelector('.owner-step-pill.active');
    if (row && pill) {
      const target = pill.offsetLeft - (row.clientWidth - pill.clientWidth) / 2;
      row.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }, [activeTab]);

  return (
    <div className="owner-form-steps">
      <div className="owner-form-steps-head">
        <span className="owner-form-steps-count">Step {index + 1} of {tabs.length}</span>
        <span className="owner-form-steps-title">{stripOptional(current.label)}</span>
        {isOptional(current.label) && <span className="owner-form-steps-optional">Optional</span>}
      </div>
      <div className="owner-form-steps-bar">
        <span style={{ width: `${((index + 1) / tabs.length) * 100}%` }} />
      </div>
      <div className="owner-form-steps-row" ref={rowRef}>
        {tabs.map((tab, i) => (
          <button
            key={tab.id}
            type="button"
            className={`owner-step-pill ${tab.id === activeTab ? 'active' : ''} ${i < index ? 'done' : ''}`}
            onClick={() => onSelect(tab.id)}
          >
            <span className="owner-step-pill-index">{i < index ? '✓' : i + 1}</span>
            {stripOptional(tab.label)}
          </button>
        ))}
      </div>
    </div>
  );
};

const isOptional = (label) => /\(optional\)/i.test(label || '');
const stripOptional = (label) => String(label || '').replace(/\s*\(optional\)/i, '');

/**
 * Footer of an owner form: Back / Next on the way through, Submit on the last
 * step, Update anywhere while editing. Cancel is a quiet link so it is not
 * pressed by mistake on a phone.
 */
export const OwnerFormActions = ({
  tabs,
  activeTab,
  onSelect,
  onNext,
  isEdit,
  loading,
  onCancel,
  submitLabel = 'Submit listing',
  draft
}) => {
  const index = tabs.findIndex((tab) => tab.id === activeTab);
  const previous = index > 0 ? tabs[index - 1] : null;
  const isLast = index === tabs.length - 1;

  return (
    <div className="owner-form-actions">
      <div className="owner-form-actions-row">
        {previous ? (
          <button type="button" className="btn btn-secondary" onClick={() => onSelect(previous.id)}>
            ← Back
          </button>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        {isEdit ? (
          <>
            {!isLast && (
              <button type="button" className="btn btn-secondary" onClick={onNext}>
                Next →
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Update'}
            </button>
          </>
        ) : isLast ? (
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting…' : submitLabel}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={onNext}>
            Next →
          </button>
        )}
      </div>
      <div className="owner-form-actions-foot">
        {previous && (
          <button type="button" className="owner-link" onClick={onCancel}>Cancel</button>
        )}
        {draft?.savedAt && (
          <span className="form-draft-status">
            {draft.synced ? 'Progress saved' : 'Saved on this device only'} {formatDraftTime(draft.savedAt)}
          </span>
        )}
      </div>
    </div>
  );
};

export default OwnerFormSteps;
